/**
 * Tiny module-level flag marking that a logout is underway.
 *
 * `AuthProvider.logout()` clears localStorage and then assigns
 * `window.location.href`, but that assignment does not stop JavaScript — the
 * current document keeps running (and keeps resolving in-flight requests) until
 * the new one commits. Anything auth-related that runs in that window sees an
 * empty localStorage and reacts as if the session had expired: `AppApiManager`
 * would re-seed `redirect_url` with the page the user just left (which then
 * makes the *next* login do a full page reload) and its refresh interceptor
 * would write its stale user snapshot back, resurrecting the cleared session.
 *
 * The flag is never reset: logout always ends in a full page load, so it only
 * has to outlive the current document.
 */
let loggingOut = false;

export const markLoggingOut = (): void => {
  loggingOut = true;
};

export const isLoggingOut = (): boolean => loggingOut;

/**
 * Module-level state that belongs to one user's session and must not survive
 * into the next one — in-memory caches, mostly. Both teardown paths (the
 * explicit logout in `AuthProvider` and the dead-session branch in
 * `AppApiManager`'s refresh interceptor) run every registered function, so a
 * module owning such state registers once here instead of each path having
 * to know about it. Registered at module load; the set is never emptied.
 */
type SessionTeardown = () => void;
const teardowns = new Set<SessionTeardown>();

export const registerSessionTeardown = (teardown: SessionTeardown): void => {
  teardowns.add(teardown);
};

export const runSessionTeardowns = (): void => {
  teardowns.forEach((teardown) => {
    try {
      teardown();
    } catch (error) {
      console.warn("Session teardown failed:", error);
    }
  });
};
